import React, {useCallback, useMemo, useState} from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';

import {getDeclarationResponsibility} from 'api/registries/gtins';
import {ReactComponent as TrashIcon} from 'assets/svg/trash.svg';

import {PermissionAccess} from 'components/common/PermissionAccess';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {isAdmin$} from 'models/auth';
import {pageNumber$} from 'models/registries/gtinsGrid/table';
import {
    deleteGtinFx,
    editGtinFx,
    isFormModalOpenChanged,
    releaseNewVersionFx,
    setApproveFx,
    setBlockFx,
    setPublishFx,
    setRecallFx,
    setSubmitFx,
    uploadDataFx,
} from 'models/registries/gtinsGrid/upload';
import {colors} from 'theme';
import {UserRoles} from 'types';
import {DeclarationResponsibility} from 'types/gtins';

import {DeclarationResponsibilityModal} from '../DeclarationResponsibilityModal';
import {DeleteProductModal} from '../DeleteProductModal';
import {GtinActionPermissions} from '../helpers/permissions';
import {RejectProductModal} from '../RejectProductModal';
import {UnlockProductModal} from '../UnlockProductModal';

type UseGtinCardControlParams = {
    editData?: Record<string, any>;
    onBeforePublish?: () => Promise<boolean>;
    onPublish?: () => Promise<unknown>;
    permissions: GtinActionPermissions;
};

type RunGtinActionOptions = {
    closeModal?: boolean;
    refreshCard?: boolean;
};

type InlineModalType = 'declaration' | 'delete' | 'reject' | 'unlock' | null;

let declarationCache: DeclarationResponsibility | null = null;
let declarationRequest: Promise<DeclarationResponsibility> | null = null;

const loadDeclarationResponsibility = async (): Promise<DeclarationResponsibility> => {
    if (declarationCache) return declarationCache;

    if (!declarationRequest) {
        declarationRequest = getDeclarationResponsibility()
            .then((response) => {
                declarationCache = response;
                return response;
            })
            .finally(() => {
                declarationRequest = null;
            });
    }

    return declarationRequest;
};

export const useGtinCardControl = (params: UseGtinCardControlParams) => {
    const {t} = useTranslation();
    const isAdmin = useStore(isAdmin$);
    const [activeInlineModal, setActiveInlineModal] = useState<InlineModalType>(null);
    const [isDeclarationChecked, setIsDeclarationChecked] = useState(false);
    const [isDeclarationLoading, setIsDeclarationLoading] = useState(false);
    const [declaration, setDeclaration] = useState<DeclarationResponsibility | null>(
        declarationCache,
    );

    const submitPending = useStore(setSubmitFx.pending);
    const approvePending = useStore(setApproveFx.pending);
    const publishPending = useStore(setPublishFx.pending);
    const blockPending = useStore(setBlockFx.pending);
    const recallPending = useStore(setRecallFx.pending);
    const releaseNewVersionPending = useStore(releaseNewVersionFx.pending);
    const deletePending = useStore(deleteGtinFx.pending);
    const uploadPending = useStore(uploadDataFx.pending);
    const pageNumber = useStore(pageNumber$);

    const isActionPending =
        submitPending ||
        approvePending ||
        publishPending ||
        blockPending ||
        recallPending ||
        releaseNewVersionPending ||
        deletePending ||
        uploadPending;

    const isInternalProduct = params.editData?.product_type === 'INTERNAL';

    const runGtinAction = useCallback(
        async (
            action: () => Promise<unknown>,
            {closeModal = false, refreshCard = true}: RunGtinActionOptions = {},
        ) => {
            try {
                await action();
                if (closeModal) isFormModalOpenChanged(false);
                if (refreshCard && params.editData?.gtin) {
                    try {
                        const payload = {
                            gtin: params.editData?.gtin,
                            productId: params.editData?.product_id,
                        };
                        await editGtinFx(payload);
                    } catch (error) {
                        console.error(error);
                    }
                }
            } catch (error) {
                console.error(error);
            }
        },
        [pageNumber, params.editData],
    );

    const closeDeclarationModal = useCallback(() => {
        setActiveInlineModal(null);
        setIsDeclarationChecked(false);
    }, []);

    const closeDeleteModal = useCallback(() => {
        setActiveInlineModal(null);
    }, []);

    const closeRejectModal = useCallback(() => {
        setActiveInlineModal(null);
    }, []);

    const closeUnlockModal = useCallback(() => {
        setActiveInlineModal(null);
    }, []);

    const openDeclarationModal = useCallback(async () => {
        setActiveInlineModal('declaration');
        setIsDeclarationChecked(false);

        if (declarationCache) {
            setDeclaration(declarationCache);
            return;
        }

        try {
            setIsDeclarationLoading(true);
            const response = await loadDeclarationResponsibility();
            setDeclaration(response);
        } catch (error) {
            console.error(error);
            setActiveInlineModal(null);
        } finally {
            setIsDeclarationLoading(false);
        }
    }, []);

    const openRejectModal = useCallback(async () => {
        setActiveInlineModal('reject');
    }, []);

    const handleOpenDeclarationModal = useCallback(async () => {
        const isPrepared = params.onBeforePublish ? await params.onBeforePublish() : true;
        if (!isPrepared) return;

        await openDeclarationModal();
    }, [openDeclarationModal, params]);

    const handlePublish = useCallback(async () => {
        closeDeclarationModal();
        await runGtinAction(
            () =>
                (isInternalProduct
                    ? setPublishFx({productId: params.editData?.product_id})
                    : params.onPublish?.()) ??
                setSubmitFx({productId: params.editData?.product_id, status: true}),
        );
    }, [closeDeclarationModal, isInternalProduct, params, runGtinAction]);

    const handleDelete = useCallback(async () => {
        if (!params.editData?.product_id) return;

        closeDeleteModal();
        await runGtinAction(() => deleteGtinFx(params.editData?.product_id), {
            closeModal: true,
        });
    }, [closeDeleteModal, params, runGtinAction]);

    const handleReject = useCallback(
        async (reason: string) => {
            if (!params.editData?.product_id) return;

            closeRejectModal();
            const payload = {
                productId: params.editData?.product_id,
                status: false,
                reason,
            };

            await runGtinAction(() => setSubmitFx(payload), {closeModal: true});
        },
        [params, closeRejectModal, runGtinAction],
    );

    const handleUnlock = useCallback(async () => {
        if (!params.editData?.product_id) return;

        closeUnlockModal();
        await runGtinAction(() =>
            setBlockFx({
                productId: params.editData?.product_id,
                status: false,
            }),
        );
    }, [closeUnlockModal, params, runGtinAction]);

    const inlineModalTitle = useMemo(() => {
        if (activeInlineModal === 'declaration') {
            return t('pages.registries.declarationResponsibility.title', {
                defaultValue: 'Declaration of Responsibility',
            });
        }

        if (activeInlineModal === 'delete') {
            return t('pages.registries.deleteProductModal.title', {
                defaultValue: 'Delete Product',
            });
        }

        return null;
    }, [activeInlineModal, t]);

    let inlineModalContent: React.ReactNode = null;

    if (activeInlineModal === 'declaration') {
        inlineModalContent = (
            <DeclarationResponsibilityModal
                declaration={declaration}
                gtin={params.editData?.product_code}
                isChecked={isDeclarationChecked}
                isLoading={isDeclarationLoading}
                isPending={isActionPending}
                onChangeChecked={setIsDeclarationChecked}
                onClose={closeDeclarationModal}
                onConfirm={handlePublish}
                renderInline
                show
            />
        );
    } else if (activeInlineModal === 'delete') {
        inlineModalContent = (
            <DeleteProductModal
                gtin={params.editData?.gtin}
                isPending={isActionPending}
                onClose={closeDeleteModal}
                onConfirm={handleDelete}
                renderInline
                show
            />
        );
    } else if (activeInlineModal === 'reject') {
        inlineModalContent = (
            <RejectProductModal
                isPending={isActionPending}
                onClose={closeRejectModal}
                onConfirm={handleReject}
                renderInline
                show
            />
        );
    } else if (activeInlineModal === 'unlock') {
        inlineModalContent = (
            <UnlockProductModal
                isPending={isActionPending}
                onClose={closeUnlockModal}
                onConfirm={handleUnlock}
                renderInline
                show
            />
        );
    }

    const actionButtons = useMemo(
        () => (
            <>
                {params.editData?.product_id && params.permissions.canDelete && (
                    <Button
                        variant={ButtonVariant.Cancel}
                        type="button"
                        disabled={isActionPending}
                        style={{marginRight: 'auto'}}
                        onClick={() => setActiveInlineModal('delete')}
                    >
                        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            <Icon
                                source={TrashIcon}
                                fillColor={isActionPending ? colors.textMuted : colors.danger}
                                width={18}
                                height={18}
                            />
                            {t('pages.registries.productCardActions.deleteProduct')}
                        </div>
                    </Button>
                )}
                {params.editData?.product_id && params.permissions.canRecall && (
                    <Button
                        variant={ButtonVariant.Outline}
                        type="button"
                        disabled={isActionPending}
                        onClick={() =>
                            runGtinAction(() =>
                                setRecallFx({productId: params.editData?.product_id}),
                            )
                        }
                    >
                        {t('pages.registries.recall')}
                    </Button>
                )}
                {params.editData?.product_id && params.permissions.canReject && (
                    <PermissionAccess
                        permissions={[]}
                        roleAccess={[UserRoles.ROLE_MPL_OPERATOR_ADMIN]}
                    >
                        <Button
                            variant={ButtonVariant.Cancel}
                            type="button"
                            disabled={isActionPending}
                            onClick={() => {
                                isAdmin
                                    ? openRejectModal()
                                    : runGtinAction(() =>
                                          setRecallFx({productId: params.editData?.product_id}),
                                      );
                            }}
                        >
                            {t('pages.registries.productCardActions.reject')}
                        </Button>
                    </PermissionAccess>
                )}
                {params.editData?.product_id && params.permissions.canBlock && (
                    <PermissionAccess
                        permissions={[]}
                        roleAccess={[UserRoles.ROLE_MPL_OPERATOR_ADMIN]}
                    >
                        <Button
                            style={{marginRight: 'auto', width: 'max-content'}}
                            variant={ButtonVariant.Cancel}
                            type="button"
                            size="md"
                            disabled={isActionPending}
                            onClick={() =>
                                runGtinAction(() =>
                                    setBlockFx({
                                        productId: params.editData?.product_id,
                                        status: true,
                                    }),
                                )
                            }
                        >
                            {t('pages.registries.productCardActions.blockCard')}
                        </Button>
                    </PermissionAccess>
                )}
                {params.editData?.product_id && params.permissions.canUnblock && (
                    <PermissionAccess
                        permissions={[]}
                        roleAccess={[UserRoles.ROLE_MPL_OPERATOR_ADMIN]}
                    >
                        <Button
                            variant={ButtonVariant.Unblock}
                            type="button"
                            disabled={isActionPending}
                            onClick={() => setActiveInlineModal('unlock')}
                        >
                            {t('pages.registries.productCardActions.unlock')}
                        </Button>
                    </PermissionAccess>
                )}
                {params.editData?.product_id && params.permissions.canApprove && (
                    <PermissionAccess
                        permissions={[]}
                        roleAccess={[UserRoles.ROLE_MPL_OPERATOR_ADMIN]}
                    >
                        <Button
                            variant={ButtonVariant.Normal}
                            type="button"
                            disabled={isActionPending}
                            onClick={() =>
                                runGtinAction(
                                    () => setApproveFx({productId: params.editData?.product_id}),
                                    {closeModal: true},
                                )
                            }
                        >
                            {t('pages.registries.productCardActions.approve')}
                        </Button>
                    </PermissionAccess>
                )}
                {params.editData?.product_id && params.permissions.canReleaseNewVersion && (
                    <Button
                        variant={ButtonVariant.Secondary}
                        type="button"
                        disabled={isActionPending}
                        onClick={() =>
                            runGtinAction(
                                () =>
                                    releaseNewVersionFx({
                                        productId: params.editData?.product_id,
                                    }),
                                {refreshCard: false},
                            )
                        }
                    >
                        {t('pages.registries.productCardActions.newVersion')}
                    </Button>
                )}
                {params.permissions.canSaveDraft && (
                    <Button
                        variant={params.editData ? ButtonVariant.Secondary : ButtonVariant.Normal}
                        type="submit"
                        disabled={isActionPending}
                    >
                        {t('components.form.saveChanges')}
                    </Button>
                )}
                {params.editData?.product_id && params.permissions.canPublish && (
                    <Button
                        variant={ButtonVariant.Normal}
                        type="button"
                        disabled={isActionPending || isDeclarationLoading}
                        onClick={handleOpenDeclarationModal}
                    >
                        {t(
                            isInternalProduct
                                ? 'components.form.publishProduct'
                                : 'components.form.sendForReview',
                        )}
                    </Button>
                )}
            </>
        ),
        [
            handleOpenDeclarationModal,
            isActionPending,
            isDeclarationLoading,
            isInternalProduct,
            params,
            t,
        ],
    );

    const closeInlineModal = useCallback(() => {
        if (activeInlineModal === 'declaration') {
            closeDeclarationModal();
        } else if (activeInlineModal === 'delete') {
            closeDeleteModal();
        } else if (activeInlineModal === 'reject') {
            closeRejectModal();
        } else if (activeInlineModal === 'unlock') {
            closeUnlockModal();
        }
    }, [
        activeInlineModal,
        closeDeclarationModal,
        closeDeleteModal,
        closeRejectModal,
        closeUnlockModal,
    ]);

    return {
        actionButtons,
        closeInlineModal,
        inlineModalContent,
        inlineModalTitle,
        isActionPending,
        isInlineModalOpen: activeInlineModal !== null,
        activeInlineModal,
    };
};
